import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/textarea/textarea.js';

import {
  ApiError,
  acceptedFileTypes,
  api,
  backingKindLabels,
  documentTypeLabels,
  formValues,
  type BackingInput,
  type BackingKind,
  type BankTransaction,
  type Document,
  type DocumentLinkInput,
  type DocumentMetadata,
  type Employee,
} from '../api.js';
import { pageStyles } from '../shared-styles.js';

const kinds = Object.entries(backingKindLabels) as [BackingKind, string][];
const receiptTypes = ['receipt', 'inbound_invoice', 'outbound_invoice', 'other'] as const;

/**
 * Backing of one bank transaction: what justifies it for the accountant,
 * plus the documents attached to it. dialog.show(tx, employees). Dispatches
 * `changed` after every saved change.
 */
@customElement('bank-transaction-dialog')
export class BankTransactionDialog extends LitElement {
  static styles = pageStyles;

  @state() private isOpen = false;
  @state() private tx: BankTransaction | null = null;
  @state() private employees: Employee[] = [];
  @state() private kind: BackingKind = 'document';
  @state() private saving = false;
  @state() private error = '';
  @state() private fieldErrors: Record<string, string> = {};
  @state() private query = '';
  @state() private candidates: Document[] = [];
  @state() private searching = false;
  private searchTimer: ReturnType<typeof setTimeout> | undefined;

  show(tx: BankTransaction, employees: Employee[]) {
    this.tx = tx;
    this.employees = employees;
    this.kind = tx.backing_kind;
    this.error = '';
    this.fieldErrors = {};
    this.query = '';
    this.candidates = [];
    this.isOpen = true;
  }

  private onHidden = (e: Event) => {
    if (e.target === e.currentTarget) this.isOpen = false;
  };

  private async refresh() {
    if (!this.tx) return;
    this.tx = await api.get<BankTransaction>(`/api/bank/transactions/${this.tx.id}`);
    this.dispatchEvent(new CustomEvent('changed', { bubbles: true, composed: true }));
  }

  private async call(fn: () => Promise<unknown>) {
    this.saving = true;
    this.error = '';
    this.fieldErrors = {};
    try {
      await fn();
      await this.refresh();
    } catch (err) {
      if (err instanceof ApiError) {
        this.error = err.message;
        this.fieldErrors = err.fields;
      } else {
        this.error = (err as Error).message;
      }
    } finally {
      this.saving = false;
    }
  }

  private async saveBacking(e: Event) {
    e.preventDefault();
    const v = formValues(e.target as HTMLFormElement);
    const body: BackingInput = {
      backing_kind: (v.backing_kind as BackingKind) ?? 'document',
      backing_note: v.backing_note || null,
      employee_id: v.backing_kind === 'employee' && v.employee_id ? Number(v.employee_id) : null,
    };
    await this.call(() => api.put(`/api/bank/transactions/${this.tx!.id}`, body));
  }

  private async upload(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const v = formValues(form);
    const fileInput = form.querySelector('input[type="file"]') as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) return;
    const metadata: DocumentMetadata = {
      type: v.type ?? 'receipt',
      title: v.title ?? '',
      document_date: v.document_date || null,
      reference: v.reference || null,
      counterparty: v.counterparty || null,
      amount: v.amount || null,
      currency: v.amount ? v.currency || null : null,
      notes: null,
      links: [{ bank_transaction_id: this.tx!.id }],
    };
    await this.call(async () => {
      const fd = new FormData();
      fd.append('metadata', JSON.stringify(metadata));
      fd.append('file', file, file.name);
      await api.upload('/api/documents', fd);
      form.reset();
    });
  }

  private onSearch(e: Event) {
    this.query = (e.target as HTMLInputElement).value;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => void this.search(), 300);
  }

  private async search() {
    const q = this.query.trim();
    if (!q) {
      this.candidates = [];
      return;
    }
    this.searching = true;
    try {
      const docs = await api.get<Document[]>(`/api/documents?q=${encodeURIComponent(q)}`);
      const attached = new Set(this.tx?.documents.map((d) => d.id));
      this.candidates = docs.filter((d) => !attached.has(d.id)).slice(0, 20);
    } catch (err) {
      this.error = (err as Error).message;
    } finally {
      this.searching = false;
    }
  }

  /** PUT the document back with its links changed; the metadata API replaces all links. */
  private async setLinks(doc: Document, links: DocumentLinkInput[]) {
    const metadata: DocumentMetadata = {
      type: doc.type,
      title: doc.title,
      document_date: doc.document_date,
      reference: doc.reference,
      counterparty: doc.counterparty,
      amount: doc.amount,
      currency: doc.currency,
      notes: doc.notes,
      links,
    };
    await api.put(`/api/documents/${doc.id}`, metadata);
  }

  private async link(doc: Document) {
    await this.call(async () => {
      await this.setLinks(doc, [...doc.links.map(toLinkInput), { bank_transaction_id: this.tx!.id }]);
      this.candidates = this.candidates.filter((c) => c.id !== doc.id);
    });
  }

  private async unlink(id: number) {
    await this.call(async () => {
      const doc = await api.get<Document>(`/api/documents/${id}`);
      await this.setLinks(
        doc,
        doc.links.filter((l) => !(l.kind === 'bank_transaction' && l.id === this.tx!.id)).map(toLinkInput),
      );
    });
  }

  render() {
    const t = this.tx;
    if (!t) return html`<wa-dialog ?open=${this.isOpen} @wa-after-hide=${this.onHidden}></wa-dialog>`;
    const err = (f: string) => this.fieldErrors[f] ?? '';
    const amount = `${t.direction === 'debit' ? '−' : '+'}${t.amount} ${t.currency}`;
    return html`
      <wa-dialog label="Transaction backing" ?open=${this.isOpen} @wa-after-hide=${this.onHidden} style="--width: min(52rem, calc(100vw - 1.5rem));">
        <p>
          <strong>${t.value_date}</strong> · ${amount}${Number(t.fee) ? html` · fee ${t.fee}` : nothing} · ${t.account_label}
          <span class="sub">${t.counterparty_name ?? ''} ${t.counterparty_account ? html`<span class="mono">${t.counterparty_account}</span>` : nothing}</span>
          <span class="sub">${t.description}${t.reference ? ` · ${t.reference}` : ''}</span>
        </p>
        ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}

        <form @submit=${this.saveBacking}>
          <wa-select name="backing_kind" label="Backing" .value=${this.kind} hint=${err('backing_kind')} @change=${(e: Event) => (this.kind = (e.target as HTMLSelectElement).value as BackingKind)}>
            ${kinds.map(([value, label]) => html`<wa-option value=${value}>${label}</wa-option>`)}
          </wa-select>
          ${this.kind === 'employee'
            ? html`
                <wa-select name="employee_id" label="Employee" required .value=${t.employee_id ? String(t.employee_id) : ''} hint=${err('employee_id')}>
                  ${this.employees.map((e) => html`<wa-option value=${String(e.id)}>${e.name}</wa-option>`)}
                </wa-select>
              `
            : this.kind === 'tax'
              ? html`
                  <p class="muted">
                    ${t.payment_reference
                      ? html`Filed under the transfer with payment reference <span class="mono">${t.payment_reference}</span>; backed when that one is.${t.tax_of ? html` Currently: ${t.tax_of}.` : ''}`
                      : 'This transaction has no payment reference to match.'}
                  </p>
                `
              : html`<div></div>`}
          <wa-textarea class="full" name="backing_note" label="Note" rows="2" .value=${t.backing_note ?? ''}></wa-textarea>
          <div class="full"><wa-button type="submit" size="small" variant="brand" ?loading=${this.saving}>Save backing</wa-button></div>
        </form>

        <h4>Documents</h4>
        ${t.documents.length
          ? html`
              <div class="table-wrap"><table class="entries">
                <tbody>
                  ${t.documents.map(
                    (d) => html`
                      <tr>
                        <td>${documentTypeLabels[d.type]}</td>
                        <td><a href=${`/api/documents/${d.id}/download`} target="_blank" rel="noopener">${d.title}</a> <span class="muted">${d.original_filename}</span></td>
                        <td class="num">${d.amount ? `${d.amount} ${d.currency ?? ''}` : ''}</td>
                        <td class="actions"><wa-button size="small" appearance="plain" variant="danger" ?disabled=${this.saving} @click=${() => this.unlink(d.id)}>Unlink</wa-button></td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table></div>
            `
          : html`<p class="muted">No document attached${t.outstanding && t.backing_kind === 'document' ? '; this transaction is outstanding' : ''}.</p>`}

        <form @submit=${this.upload}>
          <h4 class="full">Upload a receipt or invoice</h4>
          <label class="full">
            File
            <input type="file" name="file" required accept=${acceptedFileTypes} />
            <span class="sub">${err('file') || 'PDF, image, Word or Excel, up to 25 MB'}</span>
          </label>
          <wa-select name="type" label="Type" .value=${'receipt'} hint=${err('type')}>
            ${receiptTypes.map((v) => html`<wa-option value=${v}>${documentTypeLabels[v]}</wa-option>`)}
          </wa-select>
          <wa-input name="title" label="Title" required .value=${t.counterparty_name ?? ''} hint=${err('title')}></wa-input>
          <wa-input name="document_date" label="Document date" type="date" .value=${t.value_date} hint=${err('document_date')}></wa-input>
          <wa-input name="reference" label="Reference / number" .value=${''}></wa-input>
          <wa-input name="counterparty" label="Counterparty" .value=${t.counterparty_name ?? ''}></wa-input>
          <wa-input name="amount" label="Amount" inputmode="decimal" .value=${t.amount} hint=${err('amount')}></wa-input>
          <wa-select name="currency" label="Currency" .value=${t.currency} hint=${err('currency')}>
            <wa-option value="EUR">EUR</wa-option>
            <wa-option value="MKD">MKD</wa-option>
          </wa-select>
          <div class="full"><wa-button type="submit" size="small" appearance="outlined" ?loading=${this.saving}>Upload and attach</wa-button></div>
        </form>

        <h4>Attach an existing document</h4>
        <wa-input label="Search documents" placeholder="title, reference, counterparty" .value=${this.query} with-clear @input=${this.onSearch}></wa-input>
        ${this.searching ? html`<p class="muted">Searching…</p>` : nothing}
        ${this.candidates.length
          ? html`
              <div class="table-wrap"><table class="entries">
                <tbody>
                  ${this.candidates.map(
                    (d) => html`
                      <tr>
                        <td>${documentTypeLabels[d.type]}</td>
                        <td>${d.title} <span class="muted">${d.document_date ?? ''} ${d.counterparty ?? ''}</span></td>
                        <td class="num">${d.amount ? `${d.amount} ${d.currency ?? ''}` : ''}</td>
                        <td class="actions"><wa-button size="small" appearance="plain" ?disabled=${this.saving} @click=${() => this.link(d)}>Attach</wa-button></td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table></div>
            `
          : this.query && !this.searching
            ? html`<p class="muted">No other documents match.</p>`
            : nothing}

        <div slot="footer" class="footer">
          <wa-button variant="brand" @click=${() => (this.isOpen = false)}>Close</wa-button>
        </div>
      </wa-dialog>
    `;
  }
}

function toLinkInput(l: Document['links'][number]): DocumentLinkInput {
  switch (l.kind) {
    case 'employee':
      return { employee_id: l.id };
    case 'client':
      return { client_id: l.id };
    case 'contract':
      return { contract_id: l.id };
    case 'bank_account':
      return { bank_account_id: l.id };
    default:
      return { bank_transaction_id: l.id };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bank-transaction-dialog': BankTransactionDialog;
  }
}
